<template>
<div>
    <!-- Validation Alerts -->
    <BaseValidationAlerts :context="context" />

    <BaseEditPage
        :context="context"
        :alvqa="alvqa"
        :abbreviation="getMappingsDetails.abbreviation"
        cancelIconRoute="/account/mappings"
    >
        <!-- HEADLINE -->
        <template v-slot:headline="{ data }">
            <div class="d-flex align-center" style="margin-top: 8px;">
                <div :alvqa="alvqa + '-title'" style="display: flex; align-items: center;">
                    {{ $route.params.alvId === 'new' ? $t('lang_GENERAL_CAPTION_EDIT_PAGE_UNTITLED') :  data.title }}
                </div>
                <div class="ml-2" style="width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;">
                    <ContextHelpIcon 
                        v-if="hasContextHelp && (data.title || $route.params.alvId === 'new')"
                        context="AccountMappingsEdit"
                        :alvqa="alvqa + '-context-help-icon'"
                    />
                </div>
            </div>
        </template>

        <!-- FORM -->
        <template v-slot:editForm>
            <v-col cols="3">
                <v-row>
                    <BaseEditPageTextField 
                        field="Title"
                        :label="$t('lang_ACCOUNT_MAPPINGS_CAPTION_TITLE')"
                        :context="context"
                        rules="required"
                        :alvqa="alvqa + '-textfield-title'"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageDropdown 
                        field="Direction"
                        :label="$t('lang_ACCOUNT_MAPPINGS_CAPTION_DIRECTIONS')"
                        :items="mappingsDirectionsOptions"
                        :context="context"
                        rules="required"
                        :disabled="!fileUploadAllowed"
                        :clearable="false"
                        :additionalFunction="handleDirectionChange"
                        :alvqa="alvqa + '-dropdown-directions'"
                    />                    
                </v-row>
                <v-row>
                    <div class="d-flex" style="width: 100%; align-items: flex-end;">
                        <div style="flex: 1; min-width: 0;">
                            <BaseEditPageDropdown 
                                field="Domain"
                                :label="$t('lang_ACCOUNT_MAPPINGS_CAPTION_DOMAINS')" 
                                :context="context"
                                :items="mappingsDomainsOptions"
                                rules="required"
                                :disabled="!fileUploadAllowed || !isSelectedDirection"
                                :clearable="false"
                                :removeRightMargin="hasDomainContextHelp"
                                :additionalFunction="handleDomainChange"
                                :alvqa="alvqa + '-dropdown-domains'"
                            />
                        </div>
                        <div v-if="hasDomainContextHelp" style="width: 48px; flex-shrink: 0; margin-bottom: 28px; display: flex; justify-content: center;">
                            <ContextHelpIcon 
                                :context="mappingHelpContext"
                                :alvqa="alvqa + '-domain-context-help-icon'"
                            />
                        </div>
                    </div>
                </v-row>
                <v-row>
                    <BaseEditPageDropdown 
                        field="Source"
                        :label="$t('lang_GENERIC_CAPTION_SOURCE')"
                        :items="mappingsSourceOptions"
                        :context="context"
                        :disabled="(isSourceDisabled || !isSelectedDomain || isDirectionExport) && !isFromCopy"
                        :rules="isDirectionExport || isSourceDisabled ? '' : 'required'"
                        :alvqa="alvqa + '-dropdown-source'"
                    />                    
                </v-row>
                <v-row>
                    <BaseEditPageDropdown 
                        field="Shareable"
                        :label="$t('lang_GENERIC_CAPTION_SHAREABILITY')"
                        :items="mappingsShareabilityYesNo"
                        :context="context"
                        rules="required"
                        :clearable="false"
                        :alvqa="alvqa + '-dropdown-shareability'"
                    />                    
                </v-row>
            </v-col>

            <v-col>
                <v-row>
                    <BaseEditPageTextField 
                        field="AlvId"
                        :label="$t('lang_GENERIC_CAPTION_ALVID')"
                        :context="context"
                        :alvqa="alvqa + '-textfield-alvid'"
                        :clearable="false"
                        :readonly="true"
                        :disabled="true"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageTextField 
                        field="Maintainer"
                        :label="$t('lang_ACCOUNT_MAPPINGS_CAPTION_MAINTAINERS')"
                        :context="context"
                        :alvqa="alvqa + '-textfield-maintainer'"
                        :clearable="false"
                        :readonly="true"
                        :disabled="true"
                    />
                </v-row>
                <!-- <v-row>
                    <BaseEditPageTextField 
                        field="VersionNumber"
                        :label="$t('lang_GENERIC_CAPTION_VERSION_NUMBER')"
                        :context="context"
                        disable="true"
                        :alvqa="alvqa + '-textfield-version-number'"
                    />
                </v-row> -->
                <v-row>
                    <BaseEditPageTextarea 
                        field="Notes"
                        :label="$t('lang_GENERAL_CAPTION_NOTES')"
                        :context="context" 
                        rows="3"
                        :alvqa="alvqa + '-textarea-notes'"
                    />
                </v-row>

                <v-row>
                    <BaseEditPageDropdown 
                        field="Language"
                        :label="$t('lang_GENERIC_CAPTION_LANGUAGE')"
                        :items="languageOptions"
                        :context="context"
                        :clearable="true"
                        :resetValNull="true"
                        :alvqa="alvqa + '-dropdown-language'"
                    />     
                </v-row>
            </v-col>

            <v-col>
                <v-row>
                    <BaseEditPageTextField 
                        field="Abbreviation"
                        :label="$t('lang_GENERIC_CAPTION_ABBREVIATION')"
                        :context="context"
                        rules="required"
                        :alvqa="alvqa + '-textfield-abbreviation'"
                    />
                </v-row>
                <v-row>
                    <BaseEditPageDropdown 
                        field="FileFormat"
                        :label="$t('lang_GENERIC_CAPTION_FILE_FORMAT')"
                        :items="mappingsFileFormatOptions"
                        :context="context"
                        rules="required"
                        :clearable="false"
                        :additionalFunction="handleFileFormatChange"
                        :alvqa="alvqa + '-dropdown-file-format'"
                    />                    
                </v-row>
                <v-row>
                    <BaseEditPageDropdown 
                        field="Delimiter"
                        :label="$t('lang_GENERIC_CAPTION_DELIMITER')"
                        :items="mappingsDelimiterOptions"
                        :context="context"
                        :clearable="true"
                        :disabled="isDirectionExport || !notAnExcel"
                        :rules="selectedFileFormat === '.csv' && !isDirectionExport ? 'required': ''"
                        :alvqa="alvqa + '-dropdown-delimiters'"
                    />                    
                </v-row>
                <v-row v-if="!isDirectionExport && notAnExcel">
                    <BaseEditPageDropdown 
                        field="FileEncoding"
                        :label="$t('lang_ACCOUNT_MAPPINGS_CAPTION_FILE_ENCODING')"
                        :items="mappingsFileEncodingOptions"
                        :context="context"
                        :clearable="false"
                        :alvqa="alvqa + '-dropdown-file-encoding'"
                    />                    
                </v-row>
                <v-row>
                    <BaseEditPageDropdown 
                        field="DecimalSeparator"
                        :label="$t('lang_GENERIC_CAPTION_DECIMAL_SEPARATOR')"
                        :items="mappingsDecimalSeparatorOptions"
                        :context="context"
                        rules="required"
                        :clearable="false"
                        :alvqa="alvqa + '-dropdown-decimal-separator'"
                    />                    
                </v-row>
                <v-row>
                    <BaseEditPageDropdown 
                        field="Currency"
                        :label="$t('lang_GENERIC_CAPTION_CURRENCY')"
                        :items="mappingsCurrencyOptions"
                        :context="context"
                        rules="required"
                        :clearable="false"
                        :disabled="isDirectionExport"
                        :alvqa="alvqa + '-dropdown-currency'"
                    />                    
                </v-row>
                <!-- <v-row>
                    <BaseEditPageDropdown 
                        field="RightsType"
                        :label="$t('lang_GENERIC_CAPTION_RIGHTS_TYPE')"
                        :items="mappingsRightsTypes"
                        :context="context"
                        :clearable="true"
                        disabled="true"
                        :alvqa="alvqa + '-dropdown-rights-types'"
                    />                    
                </v-row> -->
            </v-col>

            <v-col>
                <v-row class="pl-4 pr-4">
                    <div class="d-flex fullWidth">
                        <div class="flex" v-if="isFilename">
                            <template>
                                <v-file-input
                                    :disabled="fileUploadAllowed"
                                    v-model="fileObj"
                                    :label="$t('lang_GENERAL_CAPTION_SELECT_FILE')"
                                    :accept="csvOrTxtAccept"
                                    @change="submitFile()"
                                    @click:clear="resetVal()"
                                    :show-size="fileObj !== null && fileObj.size !== 0"
                                    prepend-icon=""
                                    :alvqa="alvqa + '-file-upload-template-file'"
                                    :clearable="false"
                                >
                                </v-file-input>
                            </template>
                        </div>

                        <!-- v-else Mapping already has an uploaded template file and it is direction import -->
                        <div class="flex" v-else>
                            <BaseTooltip>
                                <template v-slot:content>
                                    <BaseEditPageTextField 
                                        field="FileName"
                                        :label="$t('lang_ACCOUNTS_MAPPINGS_CAPTION_TEMPLATE_PANEL')"
                                        :context="context"
                                        :clearable="false"
                                        :withMargin="false"
                                        :disabled="true"
                                        :alvqa="alvqa + '-textfield-template-file'"
                                    />
                                </template>
                                <template v-slot:text>
                                    <span>{{ getFilename }}</span>
                                </template>
                            </BaseTooltip>
                        </div>
                        <div class="pl-4 pt-4" v-if="hasDownloadPath">
                            <BaseIcon
                                @click.stop="downloadFile()" 
                                icon="cloud_download" 
                                :tooltipText="$t('lang_GENERAL_CAPTION_ICON_DOWNLOAD')" 
                                :alvqa="alvqa + '-icon-download-file-mapping'"
                                style="vertical-align: middle;" 
                            />
                        </div>
                    </div>
                </v-row>
                <v-row>
                    <BaseEditPageTextField 
                        field="Sheet"
                        :label="$t('lang_ACCOUNT_MAPPINGS_CAPTION_SHEET')"
                        :context="context"
                        :disabled="notAnExcel || isDirectionExport"
                        :alvqa="alvqa + '-textfield-sheet'"
                    />                    
                </v-row>
                <v-row>
                    <BaseEditPageDropdown 
                        field="HeaderRow"
                        :label="$t('lang_ACCOUNT_MAPPINGS_CAPTION_HEADER_ROW')"
                        :items="mappingsHeaderRowOptions"
                        :context="context"
                        :rules="isDirectionExport ? '' : 'required'"
                        :disabled="!fileUploadAllowed || isDirectionExport"
                        :clearable="false"
                        :alvqa="alvqa + '-dropdown-header-row'"
                    />                    
                </v-row>
                <v-row>
                    <BaseEditPageDropdown 
                        field="DataRow"
                        :label="$t('lang_ACCOUNT_MAPPINGS_CAPTION_DATA_ROW')"
                        :items="mappingsDataRowOptions"
                        :context="context"
                        :rules="isDirectionExport ? '' : 'required'"
                        :disabled="!fileUploadAllowed || isDirectionExport"
                        :clearable="false"
                        :alvqa="alvqa + '-dropdown-data-row'"
                    />                    
                </v-row>
            </v-col>

        </template>

        <!-- PANELS -->
        <template v-slot:panels>

            <DeletionDialog :useContextSetinVuex="true" />

            <!-- COLUMN MAPPINGS Panel -->
            <ColumnMappingsEditPanel />
            <ColumnMappingsEditPanelDialog />

        </template>
    </BaseEditPage>

    <!-- Context Help Dialog -->
    <ContextHelpDialog />
</div>
</template>

<script>
import BaseEditPage                             from '@/components/_base/BaseEditPage'
import BaseEditPageTextField                    from '@/components/_editForm/BaseEditPageTextField'
import BaseEditPageTextarea                     from '@/components/_editForm/BaseEditPageTextarea'
import BaseEditPageDropdown                     from '@/components/_editForm/BaseEditPageDropdown'
import BaseEditPageDatePicker                   from '@/components/_editForm/BaseEditPageDatePicker'
import BaseValidationAlerts                     from '@/components/_base/BaseValidationAlerts'
import BaseTooltip                              from '@/components/_base/BaseTooltip'
import BaseIcon                                 from '@/components/_buttons/BaseIcon'

import ColumnMappingsEditPanel                  from './_panels/1_columns/ColumnMappingsEditPanel'
import ColumnMappingsEditPanelDialog            from './_panels/1_columns/ColumnMappingsEditPanelDialog'
import ContextHelpIcon                          from '@/components/_buttons/ContextHelpIcon'
import ContextHelpDialog                        from '@/components/_dialogs/ContextHelpDialog'

import DeletionDialog from '@/components/_dialogs/DeletionDialog'

import api from '@/api_alv'
import { getMappingHelpContext } from '../mappingHelpContext'

import 
    { 
        DropdownAccountMappingsDirectionsOptions,
        DropdownAccountMappingsSourcesOptions,
        DropdownAccountMappingsDomainsOptions,
        DropdownAccountMappingsFileFormatOptions,
        DropdownAccountMappingsDelimiterOptions,
        DropdownAccountMappingsFileEncodingOptions,
        DropdownAccountMappingsPublishingRightsTypes,
        DropdownCurrencies,
        DropdownGenericYesNo,
        DropdownAccountMappingsDecimalSeparatorOptions,
    } 
from '@/assets/dropdowns/alvDropdowns.js'

export default {
    name: "AccountMappingsEdit",
    components: {
        BaseEditPage,
        BaseEditPageTextField,
        BaseEditPageTextarea,
        BaseEditPageDropdown,
        BaseEditPageDatePicker,
        BaseValidationAlerts,
        BaseTooltip,
        BaseIcon,

        ColumnMappingsEditPanel,
        ColumnMappingsEditPanelDialog,
        ContextHelpIcon,
        ContextHelpDialog,
        DeletionDialog,

    },
    data() {
        return {
            context: 'Mappings',
            alvqa: 'account-mappings-edit-form',
            fileObj: null,
            formData: new FormData(),
            progressBarUploadFile: false,
            hasContextHelp: false,
            hasDomainContextHelp: false
        }
    },

    computed: {
        getSelectedDirection() {
            return this.$store.getters.getMappingsDetailsDirection
        },
        getSelectedDomain() {
            return this.$store.getters.getMappingsDetailsDomain
        },
        isSelectedDirection() {
            let val = this.getSelectedDirection
            return  val !== undefined && val !== null
        },
        isSelectedDomain() {
            let val = this.getSelectedDomain
            return val !== undefined && val !== null
        },
        isFilename() {
            let fileName = this.$store.getters.getMappingsDetailsFileName
            let direction = this.$store.getters.getMappingsDetailsDirection
            return ( fileName != null && direction != 'export' ? false : true ) 
        },
        notAnExcel() {
            if (this.$store.getters.getMappingsDetailsFileFormat == "xlsx") 
                    return false
                else 
                    return true
        },
        selectedFileFormat() { return "." + this.$store.getters.getMappingsDetailsFileFormat },
        csvOrTxtAccept() {
            const fmt = this.$store.getters.getMappingsDetailsFileFormat
            if (fmt === 'csv') return '.csv,.txt'
            return this.selectedFileFormat
        },
        mappingsDomainsOptions() {
            if (!this.isSelectedDirection) return []

            // Filter out all dropdown options based on direction selected
            let arr = DropdownAccountMappingsDomainsOptions
            let filteredByDirection = arr.filter((val) => {
                return val.direction === this.getSelectedDirection
            })
            
            return filteredByDirection
        },
        mappingsDirectionsOptions() { return DropdownAccountMappingsDirectionsOptions },
        mappingsHeaderRowOptions() {
            let headerRowOptionsArray = [{ text: 'lang_GENERIC_CAPTION_NONE', value: null, alvqa: 'account-mappings-header-row-none' }]
            for (let i=0; i<30; i++) {
                headerRowOptionsArray.push({ text: (i+1).toString(), value: i+1, alvqa: 'account-mappings-header-row-' + (i+1).toString() })
            }
            return headerRowOptionsArray
        },

        mappingsCurrencyOptions() { return this.isDirectionExport ? []: DropdownCurrencies },
        mappingsDecimalSeparatorOptions() { return DropdownAccountMappingsDecimalSeparatorOptions },
        mappingsDataRowOptions() { 
            // data rows must start at least @ header row + 1
            // case of header row = none, then data rows can start at 1
            let startingValue = this.$store.getters.getMappingsDetailsHeaderRow + 1
            let dataRowOptionsArray = []
            for (let i=startingValue; i<41; i++) {
                dataRowOptionsArray.push({ text: i.toString(), value: i, alvqa: 'account-mappings-data-row-' + i.toString() })
            }
            return dataRowOptionsArray
        },
        mappingsSourceOptions() { 
            if (!this.isSelectedDomain) return []
            // different lists depending whether direction = import or export

            let direction = this.getSelectedDirection
            if (direction == "import") {
                return this.$store.getters.getAzForMappingsImport.filter(source => source.value !== 'erp-invoicing')
            } else if (direction == "export") {
                let arr = DropdownAccountMappingsSourcesOptions
                return arr.concat(this.$store.getters.getAzForMappingsExport.filter(source => source.value !== 'erp-invoicing'))
            } else return []
        },
        mappingsRightsTypes() {
            let domain = this.getSelectedDomain
            // make more generic for different mapping types
            if (domain == "publishing_income") {
                return DropdownAccountMappingsPublishingRightsTypes
            } else if (domain == "label_sales_data") {
                return []
            } else {
                return []
            }
        },
        mappingsFileFormatOptions() { return DropdownAccountMappingsFileFormatOptions },
        mappingsDelimiterOptions() { return DropdownAccountMappingsDelimiterOptions },
        mappingsFileEncodingOptions() { return DropdownAccountMappingsFileEncodingOptions },
        mappingsShareabilityYesNo() { return DropdownGenericYesNo },
        // validation based on import or export, i.e. RightsType only for Import
        isRequiredRightsType() {
            let direction = this.getSelectedDirection
            if (direction == "import") 
                return 'required'
            else 
                return ''
        },
        fileUploadAllowed() {
            let mappingAlvId    = this.$store.getters.getMappingsDetailsAlvId
            if (mappingAlvId == "ALV000000000AM")
                return true
            else
                return false
        },
        getFilename() {
            let filename = this.$store.getters.getMappingsDetailsFileName
            if (filename) return filename 
            return ''
        },
        isDirectionExport() {
            if (this.getSelectedDirection === 'export') {
                return true
            }

            return false
        },
        getMappingsDetails() {
            return this.$store.getters.getMappingsDetails
        },
        hasDownloadPath() {
            let dlPath = this.$store.getters.getMappingsDetailsDownloadPath
            return dlPath !== null && dlPath !== undefined
        },
        isSourceDisabled() {
            let domain = this.getSelectedDomain
            // Usage imports choose their source per file, in the import dialog
            return domain == "publishing_works" || domain == "label_tracks" || domain == 'label_releases' || domain == 'az_persons' || domain == 'publishing_sheets' || domain == 'publishing_projects'
                || domain == 'publishing_broadcast_events' || domain == 'publishing_usage_records';
        },
        mappingHelpContext() {
            return getMappingHelpContext(this.getSelectedDirection, this.getSelectedDomain)
        },
        isFromCopy() {
            return this.$store.getters.getLastSelectedAction === 'copy' && this.getSelectedDomain !== 'publishing_works'
        },
        languageOptions() {
            let languages = JSON.parse(JSON.stringify(this.$store.getters.getGeneralLanguages))
            return languages
        },
    },

    methods: {
        async resetVal() {
            await this.$nextTick()
            this.file = null
        },
        async submitFile() {
            if (this.fileObj) {
                this.$store.commit('setIsLoading', true)
                this.formData.append('file', this.fileObj)  
                let mappingAlvId    = this.$store.getters.getMappingsDetailsAlvId
                let fileExtension   = this.$alvPlugin.extractFileExt(this.fileObj).toLowerCase()
                let response = await api.putFile('account/mappings' + '/' + mappingAlvId, this.formData, '?templateUpload&type='+fileExtension)
                if (response.error == 1 && response.response.errorMsg) {
                    this.$store.commit('setIsLoading', false)
                    this.$store.commit('setSnackbar', {
                        type: 'error',
                        msg: this.$alvPlugin.isLangConst(response.response.errorMsg) ? i18n.t(response.response.errorMsg) : response.response.errorMsg
                    })
                    return
                }
                let fileFormat = this.$store.getters.getMappingsDetailsFileFormat
                this.$store.commit('setIsLoading', false)
                if (fileFormat == "csv" || fileFormat == "xlsx") {
                    let headerRow = this.$store.getters.getMappingsDetailsHeaderRow
                    if (headerRow) {
                        let headers = response.response.headerRow
                        headers.push('NOCOL')
                        this.$store.commit('setDropdownOfHeaders', headers)
                    }
                }
            }
        },        
        downloadFile() {
            api.getFileFromALV(this.$store.getters.getMappingsDetailsDownloadPath)
            this.$store.commit('setSnackbar', {
                type: 'success',
                msg: this.$t('lang_GENERAL_SNACKBAR_DOWNLOAD_SUCCESSFULL')
            })
        },

        handleFileFormatChange(value) {
            if (value === 'xlsx') {
                this.$store.commit('setMappingsDetailsDelimiter', null)
            }
        },
        async checkDomainContextHelp() {
            const ctx = this.mappingHelpContext
            if (!ctx) {
                this.hasDomainContextHelp = false
                return
            }
            this.hasDomainContextHelp = await this.$alvPlugin.checkContextHelpAvailability(ctx)
        },
        handleDirectionChange() {
            this.hasDomainContextHelp = false
        },
        handleDomainChange() {
            this.checkDomainContextHelp()
        }
    },
    watch: {
        getMappingsDetails: function(newVal, oldVal) {
            let headers = newVal.fileInfo
            if (headers) {
                this.$store.commit('setDropdownOfHeaders', this.$store.getters.getMappingsDetails.fileInfo.headerRow)
            }

            let filename = newVal.fileName
            if (this.fileObj === null && (filename !== null && filename !== undefined)) {
                this.fileObj = new File([''], filename, {
                    type: "text/plain"
                })
            }

            this.checkDomainContextHelp()
        },
        },
    beforeDestroy() {
        this.$store.commit('setValidationMessagesMappings', [])
    },
    async created() {
        if (this.$store.getters.getAzForMappingsImport.length === 0 || 
            this.$store.getters.getAzForMappingsExport.length === 0)
        {
            this.$store.dispatch("fetchAzForMappings")
        }
        this.hasContextHelp = await this.$alvPlugin.checkContextHelpAvailability('AccountMappingsEdit')
        this.checkDomainContextHelp()
    }
}
</script>
